// M13.7 — "See more detail" disclosure for the insights section.
//
// Mirrors the accessibility pattern established by `ui/notification.tsx`
// (useState + aria-expanded + aria-controls + hidden attribute) so we
// keep one disclosure idiom across the app — no native <details>, no
// Radix Collapsible. Callers only render this when
// `expandedParagraphs.length > 0`; the component doesn't second-guess.

import { useId, useState } from "react";
import { cn } from "@/lib/utils";

interface InsightsDisclosureProps {
  paragraphs: string[];
  /** Optional extra class for the toggle button so the parent can adjust
   *  spacing (eg. tight grid vs. the roomier dashboard card). */
  className?: string;
}

export function InsightsDisclosure({ paragraphs, className }: InsightsDisclosureProps) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const panelId = `insights-disclosure-${id}`;

  return (
    <div className={cn("mt-3", className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700 hover:text-purple-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2 rounded"
      >
        <span>{open ? "Hide detail" : "See more detail"}</span>
        <i
          className={cn(
            "fa-solid text-[11px] transition-transform",
            open ? "fa-chevron-up" : "fa-chevron-down",
          )}
          aria-hidden="true"
        />
      </button>
      <div
        id={panelId}
        hidden={!open}
        className={cn(
          "mt-3 space-y-3 border-l-2 border-purple-100 pl-4 text-sm leading-relaxed text-neutral-700",
        )}
      >
        {paragraphs.map((p, i) => (
          <p key={i} className="text-pretty">
            {p}
          </p>
        ))}
      </div>
    </div>
  );
}
